/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React from 'react';
import { Col, Row } from '@saucelabs/sl-component-grid';

import { Subscription } from '../../../records/Subscription';
import { amountSeparator } from '../../../utils/numbers';
import { SubscriptionBox } from './SubscriptionBox';
import { ConcurrencyBox } from './ConcurrencyBox';
import { PriceEntry } from './PriceEntry';
import { ApiTestingSubscriptionBox } from './ApiTestingSubscriptionBox';
import { MobileQualitySubscriptionBox } from './MobileQualitySubscriptionBox';
import Styles from './SubscriptionBoxes.styles';
import {
  TESTING_TYPES,
  PRODUCT_LIVE,
  VIRTUAL_DEVICE,
  REAL_DEVICE,
  PRODUCT_MOBILE_QUALITY,
  PRODUCT_API_TESTING,
  PRODUCT_LABELS,
} from '../../../constants';

const titleBuilder = (subscriptions: [Subscription]) => {
  const isLive = subscriptions.some((sub) => sub.plan.testing_type === PRODUCT_LIVE);
  if (isLive) {
    return PRODUCT_LABELS.LIVE;
  }

  const title = [];
  // eslint-disable-next-line camelcase
  const subscriptionTypes: Plan.testing_type & Plan.device_type = subscriptions.map((sub) => (
    sub.plan.device_type || sub.plan.testing_type
  ));
  if (subscriptionTypes.includes(VIRTUAL_DEVICE)) {
    title.push(PRODUCT_LABELS.AUTOMATED_LIVE);
  }
  if (subscriptionTypes.includes(REAL_DEVICE)) {
    title.push(PRODUCT_LABELS.REAL_DEVICE);
  }
  if (subscriptionTypes.includes(PRODUCT_MOBILE_QUALITY)) {
    title.push(PRODUCT_LABELS.MOBILE_QUALITY);
  }
  if (subscriptionTypes.includes(PRODUCT_API_TESTING)) {
    title.push(PRODUCT_LABELS.API_TESTING);
  }
  return title.join(' & ');
};

interface UnifiedPlanProps {
  subscriptions: [Subscription]
}

const UnifiedPlan: React.FC<UnifiedPlanProps> = ({ subscriptions }) => {
  const {
    otherSubscriptions,
    mobileQualitySubscription,
    apiTestingSubscription,
  } = subscriptions.reduce((acc, sub) => {
    if (sub.plan.testing_type === TESTING_TYPES.MOBILE_QUALITY) {
      acc.mobileQualitySubscription = sub;
    } else if (sub.plan.testing_type === TESTING_TYPES.API_TESTING) {
      acc.apiTestingSubscription = sub;
    } else {
      acc.otherSubscriptions.push(sub);
    }

    return acc;
  }, { otherSubscriptions: [], mobileQualitySubscription: null, apiTestingSubscription: null });

  return (
    <SubscriptionBox>
      {otherSubscriptions.length ? (
        <SubscriptionBox.Section>
          <Row>
            <Col xs={12}>
              <strong className="text-base">
                {`Current Plan: ${titleBuilder(otherSubscriptions)}`}
              </strong>
            </Col>
          </Row>
          <Row className="justify-between">
            {otherSubscriptions.map((subscription) => {
              const price = amountSeparator(subscription.getMonthlyPrice());
              const cadence = subscription.getIntervalLabel();
              return (
                <Col
                  key={subscription.id}
                  xs={4}
                >
                  <ul className={Styles.features}>
                    <li className="text-gray-600">
                      {`${cadence} plan`}
                    </li>
                    <li>
                      <ConcurrencyBox
                        concurrency={subscription.getAddonAmount()}
                        subscription={subscription}
                      />
                    </li>
                    <li>
                      <PriceEntry
                        cadence={cadence}
                        nextBillDay={subscription.displayNextBillDate()}
                        price={price}
                      />
                    </li>
                  </ul>
                </Col>
              );
            })}

            <Col
              xs={4}
            >
              <div className="mt-0.5 ml-auto text-right">
                <div>
                  Current Monthly Rate:
                  {' '}
                </div>
                <strong className="text-base">
                  {amountSeparator(
                    otherSubscriptions.reduce((acc: number, sub: Subscription) => (
                      acc + sub.getMonthlyPrice()
                    ), 0),
                  )}
                </strong>
              </div>
            </Col>
          </Row>
        </SubscriptionBox.Section>
      ) : null}

      {mobileQualitySubscription ? (
        <SubscriptionBox.Section>
          <MobileQualitySubscriptionBox subscription={mobileQualitySubscription} />
        </SubscriptionBox.Section>
      ) : null}
      {apiTestingSubscription ? (
        <SubscriptionBox.Section>
          <ApiTestingSubscriptionBox subscription={apiTestingSubscription} />
        </SubscriptionBox.Section>
      ) : null}
    </SubscriptionBox>
  );
};

export { UnifiedPlan };
